import { useState } from "react";
import { Layout } from "@/components/Layout";
import { ROUTINES } from "@/data/routines";
import { API } from "@/lib/api";
import { Barbell, House, Lightning, DownloadSimple } from "@phosphor-icons/react";
import { useAuth } from "@/contexts/AuthContext";
import { canAccess, REQUIRES } from "@/lib/plans";
import { Paywall } from "@/components/PlanGate";

const TYPE_ICONS = { gym: Barbell, home: House, bands: Lightning };
const TYPE_LABELS = { gym: "GIMNASIO", home: "EN CASA", bands: "CON LIGAS" };
const LEVELS = { beginner: "Principiante", intermediate: "Intermedio", advanced: "Avanzado" };

export default function Training() {
  const [typeFilter, setTypeFilter] = useState("all");
  const [levelFilter, setLevelFilter] = useState("all");
  const [active, setActive] = useState(null);
  const { user } = useAuth();
  const hasAccess = canAccess(user, REQUIRES.TRAINING);

  const filtered = ROUTINES.filter(
    (r) =>
      (typeFilter === "all" || r.type === typeFilter) &&
      (levelFilter === "all" || r.level === levelFilter)
  );

  const downloadPDF = async (r) => {
    const res = await fetch(`${API}/training/pdf`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        client_name: "Cliente AS TRAINING",
        routine_name: r.name,
        level: r.level,
        days: r.days,
      }),
    });
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  return (
    <Layout>
      <section className="border-b border-as-border bg-as-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="as-label text-as-green mb-3">MÓDULO 4 · ENTRENAMIENTO</p>
          <h1 className="as-heading text-5xl text-white">
            RUTINAS <span className="text-as-green">REALES.</span>
          </h1>
        </div>
      </section>

      {!hasAccess ? (
        <Paywall planName="AS START" />
      ) : (
      <section className="bg-as-surface py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="flex flex-wrap gap-6 mb-10">
            <FilterGroup
              label="Tipo"
              value={typeFilter}
              onChange={setTypeFilter}
              options={[{ v: "all", l: "Todos" }, { v: "gym", l: "Gimnasio" }, { v: "home", l: "Casa" }, { v: "bands", l: "Ligas" }]}
              testid="filter-type"
            />
            <FilterGroup
              label="Nivel"
              value={levelFilter}
              onChange={setLevelFilter}
              options={[{ v: "all", l: "Todos" }, { v: "beginner", l: "Principiante" }, { v: "intermediate", l: "Intermedio" }, { v: "advanced", l: "Avanzado" }]}
              testid="filter-level"
            />
          </div>

          {/* Routine cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((r) => {
              const Icon = TYPE_ICONS[r.type];
              return (
                <div
                  key={r.id}
                  onClick={() => setActive(r)}
                  className="as-card cursor-pointer hover:border-as-green transition-all"
                  data-testid={`routine-${r.id}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <Icon size={32} weight="duotone" color="#39FF14" />
                    <span className="as-label text-[10px]">{TYPE_LABELS[r.type]}</span>
                  </div>
                  <h3 className="as-heading text-2xl text-white mb-2">{r.name.toUpperCase()}</h3>
                  <p className="text-sm text-as-gray mb-4">{r.description}</p>
                  <div className="flex justify-between items-center">
                    <span className="as-label text-as-green">{LEVELS[r.level]}</span>
                    <span className="as-label">{r.days.length} días</span>
                  </div>
                </div>
              );
            })}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-16 text-as-gray">
              <p>No hay rutinas con esos filtros.</p>
            </div>
          )}
        </div>
      </section>
      )}

      {/* Modal */}
      {active && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setActive(null)}
          data-testid="routine-modal"
        >
          <div
            className="bg-as-surface border-2 border-as-green max-w-3xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8 border-b border-as-border flex justify-between items-start gap-4">
              <div>
                <p className="as-label text-as-green mb-2">{TYPE_LABELS[active.type]} · {LEVELS[active.level]}</p>
                <h2 className="as-heading text-3xl text-white">{active.name.toUpperCase()}</h2>
              </div>
              <button onClick={() => setActive(null)} className="text-white text-2xl">✕</button>
            </div>
            <div className="p-8 space-y-6">
              {active.days.map((d) => (
                <div key={d.name}>
                  <h3 className="as-heading text-xl text-as-green mb-3">{d.name}</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="as-label text-left">
                        <th className="py-2">Ejercicio</th>
                        <th className="py-2">Series</th>
                        <th className="py-2">Reps</th>
                        <th className="py-2">Descanso</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-as-border">
                      {d.exercises.map((ex) => (
                        <tr key={`${d.name}-${ex.name}`}>
                          <td className="py-2 text-white">{ex.name}</td>
                          <td className="py-2 text-as-gray">{ex.sets}</td>
                          <td className="py-2 text-as-gray">{ex.reps}</td>
                          <td className="py-2 text-as-gray">{ex.rest}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
              <button onClick={() => downloadPDF(active)} className="as-btn-primary w-full" data-testid="download-routine-pdf">
                Descargar rutina PDF <DownloadSimple size={18} weight="bold" />
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const FilterGroup = ({ label, value, onChange, options, testid }) => (
  <div>
    <p className="as-label mb-2">{label}</p>
    <div className="flex gap-2 flex-wrap">
      {options.map((o) => (
        <button
          key={o.v}
          onClick={() => onChange(o.v)}
          className={`px-4 py-2 text-xs uppercase tracking-wider font-bold transition-colors ${
            value === o.v ? "bg-as-green text-as-black" : "bg-as-surface text-white border border-as-border hover:border-as-green"
          }`}
          data-testid={`${testid}-${o.v}`}
        >
          {o.l}
        </button>
      ))}
    </div>
  </div>
);
